import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar/Navbar";
import Footer from "@/components/Footer/Footer";
import { postsByLang, formatPostDate, type Post } from "@/data/posts";
import { useLocale } from "@/i18n/useLocale";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useGsapReveal } from "@/hooks/useGsapReveal";
import "./News.css";

/** Notas en tarjetas debajo de la destacada. Seis llenan filas completas de 2 y de 3 columnas. */
const RECENT_COUNT = 6;

/**
 * Listado de novedades. Cada nota se pre-renderiza a su propio HTML.
 *
 * De arriba a abajo: filtro por tema, la nota mas reciente destacada, las seis
 * siguientes en tarjetas y el resto como archivo en filas. Las notas nunca se
 * ocultan: el filtro solo cambia cuales se muestran, y el HTML pre-renderizado
 * trae todas.
 */
function News() {
  const { t, locale, path } = useLocale();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const posts = useMemo(() => postsByLang(locale), [locale]);

  // El tema elegido se guarda con su idioma: los nombres cambian entre ES y EN,
  // y asi un cambio de idioma vuelve a "Todas" sin un efecto que lo resetee.
  const [selected, setSelected] = useState<{ locale: string; tag: string } | null>(null);
  const activeTag = selected?.locale === locale ? selected.tag : null;

  const topics = useMemo(() => {
    const counts = new Map<string, number>();
    for (const post of posts) counts.set(post.tag, (counts.get(post.tag) ?? 0) + 1);
    return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], locale));
  }, [posts, locale]);

  const visible = activeTag ? posts.filter((post) => post.tag === activeTag) : posts;
  const [featured, ...rest] = visible;
  const recent = rest.slice(0, RECENT_COUNT);
  const archive = rest.slice(RECENT_COUNT);

  // Solo se rearma al cambiar de idioma. Al filtrar no: las tarjetas que
  // aparecen son nodos nuevos, ya visibles, y rearmar el efecto con la pagina
  // scrolleada deja en opacidad 0 las que quedaron por encima de la pantalla.
  const resultsRef = useGsapReveal<HTMLDivElement>({
    selector: ".post-featured, .post-card",
    stagger: 0.06,
    deps: [locale],
  });

  const href = (post: Post) => `${path("news")}/${post.slug}`;
  const countLabel =
    visible.length === 1 ? t.news.countOne : t.news.countMany.replace("{n}", String(visible.length));

  return (
    <div className="news-page">
      <Navbar />

      <main id="main-content">
        {/* Masthead negro: el navbar es fijo y transparente arriba de todo, y el
            logo de WAIA es claro (luminancia 216). Sobre el blanco de la pagina
            quedaba invisible. Al scrollear el navbar toma fondo oscuro solo. */}
        <header className="news-page__masthead">
          <div className="news-page__masthead-inner" ref={headerRef}>
            <p className="news-page__eyebrow">{t.news.eyebrow}</p>
            <h1 className="news-page__title">{t.news.pageTitle}</h1>
            <p className="news-page__intro">{t.news.pageIntro}</p>
          </div>
        </header>

        <section className="news-page__content">
          {posts.length === 0 ? (
            <p className="news-page__empty">{t.news.empty}</p>
          ) : (
            <>
              <div className="news-filter" role="group" aria-label={t.news.filterLabel}>
                <button
                  type="button"
                  className="news-filter__chip"
                  aria-pressed={activeTag === null}
                  onClick={() => setSelected(null)}
                >
                  {t.news.allTopics}
                  <span className="news-filter__count">{posts.length}</span>
                </button>
                {topics.map(([tag, count]) => (
                  <button
                    key={tag}
                    type="button"
                    className="news-filter__chip"
                    aria-pressed={activeTag === tag}
                    onClick={() => setSelected({ locale, tag })}
                  >
                    {tag}
                    <span className="news-filter__count">{count}</span>
                  </button>
                ))}
              </div>

              <p className="news-page__count" aria-live="polite">
                {countLabel}
              </p>

              <div ref={resultsRef}>
                {featured && (
                  <Link to={href(featured)} className="post-featured">
                    <span className="post-featured__badge">{t.news.latest}</span>
                    <span className="post-card__meta">
                      <span className="post-card__tag">{featured.tag}</span>
                      <time dateTime={featured.date} className="post-card__date">
                        {formatPostDate(featured.date, locale)}
                      </time>
                    </span>
                    <h2 className="post-featured__title">{featured.title}</h2>
                    <p className="post-featured__summary">{featured.summary}</p>
                    <span className="post-card__footer">
                      <span className="post-card__cta">{t.news.read}</span>
                      <span className="post-card__reading">
                        {featured.readingMinutes} {t.news.readingTime}
                      </span>
                    </span>
                  </Link>
                )}

                {recent.length > 0 && (
                  <>
                    <h2 className="news-page__subtitle">{t.news.recent}</h2>
                    <ul className="news-page__list">
                      {recent.map((post) => (
                        <li key={post.slug}>
                          <Link to={href(post)} className="post-card">
                            <span className="post-card__meta">
                              <span className="post-card__tag">{post.tag}</span>
                              <time dateTime={post.date} className="post-card__date">
                                {formatPostDate(post.date, locale)}
                              </time>
                            </span>

                            <h3 className="post-card__title">{post.title}</h3>
                            <p className="post-card__summary">{post.summary}</p>

                            <span className="post-card__footer">
                              <span className="post-card__cta">{t.news.read}</span>
                              <span className="post-card__reading">
                                {post.readingMinutes} {t.news.readingTime}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              {archive.length > 0 && (
                <>
                  <h2 className="news-page__subtitle">{t.news.archive}</h2>
                  <ul className="news-archive">
                    {archive.map((post) => (
                      <li key={post.slug}>
                        <Link to={href(post)} className="news-row">
                          <span className="news-row__meta">
                            <span className="post-card__tag">{post.tag}</span>
                            <time dateTime={post.date} className="post-card__date">
                              {formatPostDate(post.date, locale)}
                            </time>
                          </span>
                          <h3 className="news-row__title">{post.title}</h3>
                          <span className="news-row__arrow" aria-hidden="true">
                            →
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default News;
